import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import DashboardDoctorHeader from "../../components/MainHeader/DashboardDoctorHeader";
import { useUserContext } from "../../context/UserContext";
import AppStoreIcon from "../../assets/images/AppStore.svg";
import PlayIcon from "../../assets/images/play.svg";
import DownloadIcon from "../../assets/images/download.svg";
import VideoIcon from "../../assets/images/video-icon.svg";
import { helpCenterApi, getHelpCenterSettingsApi } from "../../services/apiService";
import { IMAGE_URL, ADMIN_DASHBOARD_URL } from "../../app_url";
import "./HelpCenter.scss";

const HelpCenter = () => {
  const { sidebarDrawerOpen } = useUserContext();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [tutorials, setTutorials] = useState([]);
  
  // State for Barcode Modal
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [secretCode, setSecretCode] = useState("");
  const [qrUrl, setQrUrl] = useState("");

  // State for Coming Soon Modal
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [comingSoonData, setComingSoonData] = useState({ title: "", message: "", date: "" });

  useEffect(() => {
    fetchHelpCenterData();
  }, []);

  const generateBarcode = () => {
    // Generate a random 4-digit secret code
    const code = Math.floor(1000 + Math.random() * 9000).toString();
    setSecretCode(code);
    
    // Include the code in the QR URL (encoded) so the validation page knows what to expect
    const verificationToken = btoa(code).replace(/=/g, "");
    const validationLink = `${window.location.origin}/scan-app-download?v=${verificationToken}`; 
    setQrUrl(validationLink);
    
    setShowBarcodeModal(true);
  };

  const comingSoonAndroid = () => {
    setComingSoonData({
      title: "Version Android",
      message: "Notre application arrive très prochainement",
      date: "1 juillet 2026"
    });
    setShowComingSoonModal(true);
  };

  const comingSoonDesktop = () => {
    setComingSoonData({
      title: "Version Offline",
      message: "Notre application arrive très prochainement",
      date: "1 août 2026"
    });
    setShowComingSoonModal(true);
  };

  const fetchHelpCenterData = async () => {
    try {
      const response = await getHelpCenterSettingsApi();
      if (response.data.res) {
        setSettings(response.data.settings);
        setTutorials(response.data.tutorials || []);
      }
    } catch (error) {
      console.error("Error fetching help center data:", error);
      setTutorials([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    
    // Tutorial images are uploaded via admin-dashboard
    // So we try ADMIN_DASHBOARD_URL first, then fallback to IMAGE_URL
    const baseUrl = ADMIN_DASHBOARD_URL || IMAGE_URL;
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    return `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}${cleanPath}`;
  };

  const applications = [
    {
      name: "App Scan",
      description: "Télécharger l'application de scan",
      icon: PlayIcon,
      buttonText: "Google Play",
      buttonClass: "google-play",
      onClick: generateBarcode,
    },
    {
      name: "Version Android",
      description: "Télécharger la version Android",
      icon: PlayIcon,
      buttonText: "Google Play",
      buttonClass: "google-play",
      onClick: comingSoonAndroid,
    },
    {
      name: "Version Offline",
      description: "Accéder à la version offline",
      icon: DownloadIcon,
      buttonText: "Télécharger",
      buttonClass: "download",
      onClick: comingSoonDesktop,
    },
  ];

  return (
    <div className="doctormain">
      <Sidebar />
      <div className={`rightSec ${sidebarDrawerOpen ? "rightOpenSideMenu" : ""}`}>
        <DashboardDoctorHeader hideWelcome={true} hideSearch={true} hideProfile={true} />
        <div className="mainDiv help-center-container">
          <div className="help-center-header">
            <h1>Centre d'aide Doctolik</h1>
            <p>Tutoriels vidéo et téléchargements des applications Doctolik</p>
          </div>

          <div className="help-section apps-section">
            <h2 className="section-title">Télécharger nos applications</h2>
            <div className="apps-grid">
              {applications.map((app, index) => (
                <div key={index} className="app-card">
                  <div className="app-info">
                    <h3>{app.name}</h3>
                    <p>{app.description}</p>
                  </div>
                  {app.onClick ? (
                    <button 
                      onClick={app.onClick}
                      className={`app-btn ${app.buttonClass}`}
                      style={{ textDecoration: 'none', display: 'inline-block' }}
                    >
                      {app.buttonText}
                    </button>
                  ) : (
                    <a 
                      href={app.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={`app-btn ${app.buttonClass}`}
                      style={{ textDecoration: 'none', display: 'inline-block' }}
                    >
                      {app.buttonText}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Barcode Modal */}
          {showBarcodeModal && (
            <div className="barcode-modal-overlay" onClick={() => setShowBarcodeModal(false)}>
              <div className="barcode-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-modal" onClick={() => setShowBarcodeModal(false)}>&times;</button>
                <h3>App Scan</h3>
                <p>Scanner pour télécharger l'application</p>
                <div className="qr-container">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`} 
                    alt="QR Code" 
                  />
                </div>
                <div className="secret-code-section">
                  <p>Code Secret :</p>
                  <div className="code-display">{secretCode}</div>
                </div>
              </div>
            </div>
          )}

          {/* Coming Soon Modal */}
          {showComingSoonModal && (
            <div className="barcode-modal-overlay" onClick={() => setShowComingSoonModal(false)}>
              <div className="coming-soon-modal-content" onClick={(e) => e.stopPropagation()}>
                <button className="close-modal" onClick={() => setShowComingSoonModal(false)}>&times;</button>
                <div className="modal-inner-border">
                  <div className="rocket-icon">🚀</div>
                  <h3 className="coming-soon-title">Coming Soon</h3>
                  <p className="coming-soon-description">{comingSoonData.message}</p>
                  <div className="coming-soon-date">
                    <span className="calendar-icon">📅</span>
                    <span className="date-text">{comingSoonData.date}</span>
                  </div>
                  <p className="coming-soon-footer">Merci pour votre confiance</p>
                </div>
              </div>
            </div>
          )}

          <div className="help-section tutorials-section">
            <h2 className="section-title">Tutoriels Vidéo</h2>
            <div className="tutorials-grid">
              {tutorials.length > 0 ? (
                tutorials.map((tutorial, index) => (
                  <div key={index} className="tutorial-card">
                    <div className="tutorial-image">
                      {tutorial.image && getImageUrl(tutorial.image) ? (
                        <>
                          <img 
                            src={getImageUrl(tutorial.image)} 
                            alt={tutorial.title} 
                            className="main-tutorial-img"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.classList.add('no-image');
                              // Show the play-overlay if image fails to load
                              const overlay = e.target.parentElement.querySelector('.play-overlay');
                              if (overlay) overlay.style.display = 'flex';
                            }}
                          />
                          <div className="play-overlay" style={{ display: 'none' }}>
                            <img src={VideoIcon} alt="Play" />
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="no-image-placeholder"></div>
                          <div className="play-overlay" style={{ display: 'flex' }}>
                            <img src={VideoIcon} alt="Play" />
                          </div>
                        </>
                      )}
                    </div>
                    <div className="tutorial-info">
                      <h3>{tutorial.title}</h3>
                      <p>Durée : {tutorial.duration}</p>
                      <a 
                        href={tutorial.youtube_link} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="view-tutorial-btn"
                        style={{ textDecoration: 'none', textAlign: 'center' }}
                      >
                        Voir le tutoriel
                      </a>
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ textAlign: 'center', gridColumn: '1 / -1' }}>Aucun tutoriel disponible pour le moment.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
